import React, { FC, useCallback, useEffect, useState } from "react";
import TokenService from "services/token.service";
import config, { API_BASE_URL_KEY } from "config";

const POLL_MS = 60_000;

interface SseFrame {
  event: string;
  data: any;
}

function parseSseFrames(buffer: string): { frames: SseFrame[]; rest: string } {
  const frames: SseFrame[] = [];
  const parts = buffer.split("\n\n");
  const rest = parts.pop() ?? "";
  for (const part of parts) {
    let event = "message";
    let dataLine = "";
    for (const line of part.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
    }
    if (!dataLine) continue;
    try {
      frames.push({ event, data: JSON.parse(dataLine) });
    } catch {
      // skip malformed frame
    }
  }
  return { frames, rest };
}

const MessagesUnreadBadge: FC = () => {
  const [count, setCount] = useState<number>(0);
  const [isAuthed, setIsAuthed] = useState<boolean>(
    () => !!TokenService.getLocalAccessToken()
  );
  const baseUrl = config.get(API_BASE_URL_KEY);

  // Watch token presence so the badge mounts after login.
  useEffect(() => {
    const t = setInterval(() => {
      setIsAuthed(!!TokenService.getLocalAccessToken());
    }, 5_000);
    return () => clearInterval(t);
  }, []);

  const fetchCount = useCallback(async () => {
    const token = TokenService.getLocalAccessToken();
    if (!token) return;
    try {
      const resp = await fetch(`${baseUrl}/inbox/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const n = Number(data?.unread ?? 0);
      setCount(Number.isFinite(n) ? n : 0);
    } catch {
      // network blip, leave count alone
    }
  }, [baseUrl]);

  useEffect(() => {
    if (!isAuthed) return;
    void fetchCount();
    const t = setInterval(() => void fetchCount(), POLL_MS);
    // The Inbox page dispatches this when the operator opens a
    // conversation. Subtract the conversation's prior unread count
    // optimistically so the badge updates instantly, then re-fetch in
    // the background to reconcile with server-side state.
    const onMarkedRead = (e: Event) => {
      const delta = Number(
        (e as CustomEvent<{ unreadDelta?: number }>).detail?.unreadDelta ?? 0
      );
      if (Number.isFinite(delta) && delta > 0) {
        setCount((prev) => Math.max(0, prev - delta));
      }
      void fetchCount();
    };
    const onMarkedAllRead = () => {
      setCount(0);
      void fetchCount();
    };
    window.addEventListener("inbox:marked-read", onMarkedRead);
    window.addEventListener("inbox:marked-all-read", onMarkedAllRead);
    return () => {
      clearInterval(t);
      window.removeEventListener("inbox:marked-read", onMarkedRead);
      window.removeEventListener("inbox:marked-all-read", onMarkedAllRead);
    };
  }, [isAuthed, fetchCount]);

  // Subscribe to SSE so the badge bumps the moment a new inbound arrives.
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    let ctrl: AbortController | null = null;
    let backoffMs = 1_000;

    const connect = async () => {
      if (cancelled) return;
      const token = TokenService.getLocalAccessToken();
      if (!token) return;

      ctrl = new AbortController();
      try {
        const resp = await fetch(`${baseUrl}/ai-builder/notifications/stream`, {
          signal: ctrl.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
        });
        if (!resp.ok || !resp.body) {
          throw new Error(`stream HTTP ${resp.status}`);
        }
        backoffMs = 1_000;

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";

        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const { frames, rest } = parseSseFrames(buf);
          buf = rest;
          for (const frame of frames) {
            if (
              frame.event === "inbox_update" ||
              frame.event === "inbox_marked_read" ||
              frame.event === "inbox_marked_all_read"
            ) {
              void fetchCount();
            }
          }
        }
      } catch (e: any) {
        if (cancelled || e?.name === "AbortError") return;
      }

      if (cancelled) return;
      await new Promise((r) => setTimeout(r, backoffMs));
      backoffMs = Math.min(backoffMs * 2, 30_000);
      void connect();
    };

    void connect();
    return () => {
      cancelled = true;
      ctrl?.abort();
    };
  }, [isAuthed, baseUrl, fetchCount]);

  if (count <= 0) return null;
  return (
    <span className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full border border-[var(--danger-border)] bg-[var(--danger-soft)] px-1 text-[10px] font-bold leading-none text-[var(--danger)]">
      {count > 99 ? "99+" : count}
    </span>
  );
};

export default MessagesUnreadBadge;
